export type TreeCheckingMode = 'PROPAGATE_PRESERVING_UNCHECK' | 'SIMPLE' | 'SINGLE'

export type TreeCheckingNode = {
  key: string
  children?: TreeCheckingNode[]
}

export function normalizeTreeCheckingMode(raw: unknown): TreeCheckingMode {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (s === 'SIMPLE' || s === 'SINGLE') return s
  return 'PROPAGATE_PRESERVING_UNCHECK'
}

export function isTreeSelectionKeyChecked(
  keys: Record<string, unknown> | null | undefined,
  key: string,
): boolean {
  if (!keys || !key) return false
  const v = keys[key]
  if (v == null || v === false) return false
  if (typeof v === 'object' && v !== null && 'checked' in v) {
    return Boolean((v as { checked?: unknown }).checked)
  }
  return Boolean(v)
}

function collectDescendantKeys(node: TreeCheckingNode, into: string[]): void {
  if (!node.children?.length) return
  for (const child of node.children) {
    into.push(child.key)
    collectDescendantKeys(child, into)
  }
}

function findNode(list: TreeCheckingNode[], key: string): TreeCheckingNode | null {
  for (const n of list) {
    if (n.key === key) return n
    if (n.children?.length) {
      const found = findNode(n.children, key)
      if (found) return found
    }
  }
  return null
}

function toggledKeys(
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
): { checked: string[]; unchecked: string[] } {
  const checked: string[] = []
  const unchecked: string[] = []
  const all = new Set([...Object.keys(prev), ...Object.keys(next)])
  for (const key of all) {
    const was = isTreeSelectionKeyChecked(prev, key)
    const now = isTreeSelectionKeyChecked(next, key)
    if (was === now) continue
    if (now) checked.push(key)
    else unchecked.push(key)
  }
  return { checked, unchecked }
}

function toBooleanKeys(keep: Set<string>): Record<string, boolean> {
  const out: Record<string, boolean> = {}
  for (const key of keep) out[key] = true
  return out
}

/** Ключи, которые не являются потомками другого ключа из того же набора (клик, не каскад PrimeVue). */
function rootToggles(keys: string[], tree: TreeCheckingNode[]): string[] {
  return keys.filter((key) => {
    for (const other of keys) {
      if (other === key) continue
      const otherNode = findNode(tree, other)
      if (!otherNode) continue
      const descendants: string[] = []
      collectDescendantKeys(otherNode, descendants)
      if (descendants.includes(key)) return false
    }
    return true
  })
}

/**
 * Swing DefaultTreeCheckingModel:
 * SIMPLE — без каскада; SINGLE — один узел; PROPAGATE_PRESERVING_UNCHECK — check каскад, uncheck нет.
 */
export function applyTreeCheckingModeSelection(options: {
  mode: TreeCheckingMode
  prev: Record<string, unknown>
  next: Record<string, unknown>
  tree: TreeCheckingNode[]
  isSelective: (key: string) => boolean
}): Record<string, boolean> {
  const { mode, prev, next, tree, isSelective } = options
  const { checked, unchecked } = toggledKeys(prev, next)

  if (mode === 'SINGLE') {
    const keep = new Set<string>()
    const roots = rootToggles(checked, tree)
    const lastChecked = roots.length ? roots[roots.length - 1] : null
    if (lastChecked && isSelective(lastChecked)) keep.add(lastChecked)
    else {
      for (const key of Object.keys(next)) {
        if (isTreeSelectionKeyChecked(next, key) && isSelective(key)) {
          keep.add(key)
          break
        }
      }
    }
    return toBooleanKeys(keep)
  }

  if (mode === 'SIMPLE') {
    const keep = new Set<string>()
    for (const key of Object.keys(prev)) {
      if (isTreeSelectionKeyChecked(prev, key) && isSelective(key)) keep.add(key)
    }
    for (const key of rootToggles(checked, tree)) {
      if (isSelective(key)) keep.add(key)
    }
    for (const key of rootToggles(unchecked, tree)) {
      keep.delete(key)
    }
    return toBooleanKeys(keep)
  }

  const keep = new Set<string>()
  for (const key of Object.keys(prev)) {
    if (isTreeSelectionKeyChecked(prev, key) && isSelective(key)) keep.add(key)
  }
  for (const key of Object.keys(next)) {
    if (isTreeSelectionKeyChecked(next, key) && isSelective(key)) keep.add(key)
  }
  for (const key of checked) {
    if (!isSelective(key)) continue
    keep.add(key)
    const node = findNode(tree, key)
    if (node) {
      const descendants: string[] = []
      collectDescendantKeys(node, descendants)
      for (const d of descendants) {
        if (isSelective(d)) keep.add(d)
      }
    }
  }
  for (const key of rootToggles(unchecked, tree)) {
    keep.delete(key)
  }
  return toBooleanKeys(keep)
}
